import { useAsyncData, useState } from '#imports'
import { computed, onMounted } from 'vue'

import { useUserStore } from '@backmarket/nuxt-layer-oauth/useUserStore'
import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'
import { useMarketplace } from '@backmarket/nuxt-module-marketplace/useMarketplace'
import { useLocalStorage } from '@backmarket/utils/composables/useLocalStorage'
import { camelizeKeys } from '@backmarket/utils/object/camelizeKeys'

export function useDeliveryPostalCode() {
  const userStore = useUserStore()
  const $httpFetch = useHttpFetch()
  const localStorage = useLocalStorage()
  const { countryCode } = useMarketplace().market

  const storageKey = `bm.deliveryZipCode.${countryCode}`

  const { data: savedAddress, refresh } = useAsyncData(
    'deliveryZipCode.userAddress',
    async () => {
      if (localStorage.getItem(storageKey)) return null
      if (!userStore.isUserAuthenticatedClient.value) return null

      return $httpFetch('/bm/client/addresses/shipping')
    },
    {
      lazy: true,
      server: false,
      watch: [userStore.isUserAuthenticatedClient],
    },
  )

  const selectedPostalCode = useState<string | null>(
    `deliveryZipCode.${countryCode}`,
    () => null,
  )

  onMounted(() => {
    if (selectedPostalCode.value === null) {
      selectedPostalCode.value = localStorage.getItem(storageKey)
    }
  })

  function setUserSelectedPostalCode(value: string) {
    localStorage.setItem(storageKey, value)
    selectedPostalCode.value = value
  }

  function clearUserSelectedPostalCode() {
    localStorage.removeItem(storageKey)
    selectedPostalCode.value = null

    return refresh()
  }

  async function validatePostalCode(value: string) {
    const { postalCode } = camelizeKeys(
      await $httpFetch('/bm/shipping/address-validation/v1/postal-code', {
        method: 'POST',
        body: { postal_code: value, country_code: countryCode },
      }),
    )

    return postalCode
  }

  const addressPostalCode = computed(() =>
    savedAddress.value?.country === countryCode
      ? savedAddress.value?.postalCode
      : undefined,
  )

  return {
    postalCode: computed(() => {
      if (selectedPostalCode.value) {
        return selectedPostalCode.value
      }

      return addressPostalCode.value
    }),
    addressPostalCode,
    setPostalCode: setUserSelectedPostalCode,
    clearPostalCode: clearUserSelectedPostalCode,
    validatePostalCode,
    refresh,
  }
}
